import { useMemo, useState } from 'react';
import { useAccelerationTierEnabled } from '../../api/capabilities.js';
import { useIsInternalAdmin } from '../../api/me.js';
import {
  useCubes,
  useCubeBuilds,
  useCubeHealth,
  useTriggerCubeBuild,
  useCubeBackfills,
  useCreateCubeBackfill,
  useBackfillAction,
  type CubeSummary,
  type CubeHealthRow,
  type CubeBuildStatus,
  type CubeBackfill,
} from '../../api/cubes.js';
import { fmtDataDate } from '../../lib/format.js';
import { DIAGNOSIS_META, LIFECYCLE_META, cubeNeedsAttention } from '../../lib/cube-health.js';

/**
 * Admin → Cubes (CSAGE-152).
 *
 * Tenant-wide view of every registered cube — platform tier first,
 * then customer tier. For each cube:
 *
 *   - Title, source kind, dimensions/measures count, pre-aggs
 *   - Refresh cron + last successful build timestamp
 *   - Status pill (running / succeeded / failed / never)
 *   - Newest data date next to the status pill; a stale cube gets a
 *     "Stale" label and a warning edge (CSAGE-502). Informational only:
 *     the status pill and every action behave as before.
 *   - "Refresh now" buttons (incremental + backfill)
 *   - Click row to expand build history
 *
 * Read endpoints poll on a 30s/10s rhythm so a running build feels
 * live. Trigger endpoints invalidate the relevant queries on success.
 *
 * Roles:
 *   - View: any authenticated user (matches GET /cubes)
 *   - Refresh: TenantAdmin / DashboardAdmin / ReportAdmin (matches
 *              POST /cubes/:name/builds)
 */
export function CubesAdminPage(): JSX.Element {
  const { data: cubes, isLoading, error } = useCubes();
  // Canopy-admin enrichment (CSAGE-543): the diagnosis + upstream-feed view
  // is internal-admin only server-side, so a customer admin keeps the plain
  // build-status console and never 403-polls the health endpoint.
  const isInternalAdmin = useIsInternalAdmin();
  const { data: health } = useCubeHealth(isInternalAdmin);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [tierFilter, setTierFilter] = useState<'all' | 'platform' | 'customer'>('all');
  const [attentionOnly, setAttentionOnly] = useState(false);

  const healthByName = useMemo(
    () => new Map<string, CubeHealthRow>((health ?? []).map((h) => [h.name, h])),
    [health],
  );

  const filtered = useMemo(() => {
    let list = cubes ?? [];
    if (tierFilter !== 'all') list = list.filter((c) => c.tier === tierFilter);
    if (attentionOnly) {
      list = list.filter((c) => {
        const h = healthByName.get(c.name);
        return h ? cubeNeedsAttention(h) : false;
      });
    }
    // Problems float to the top when we have a diagnosis; otherwise the
    // server's order (platform then customer) is preserved.
    if (healthByName.size === 0) return list;
    return [...list].sort((a, b) => {
      const ra = DIAGNOSIS_META[healthByName.get(a.name)?.diagnosis ?? 'not_checked'].rank;
      const rb = DIAGNOSIS_META[healthByName.get(b.name)?.diagnosis ?? 'not_checked'].rank;
      return ra - rb;
    });
  }, [cubes, tierFilter, attentionOnly, healthByName]);

  const stats = useMemo(() => {
    const list = cubes ?? [];
    return {
      total: list.length,
      platform: list.filter((c) => c.tier === 'platform').length,
      customer: list.filter((c) => c.tier === 'customer').length,
      neverBuilt: list.filter((c) => c.lastBuildStatus === null).length,
      failing: list.filter((c) => c.lastBuildStatus === 'failed').length,
      running: list.filter((c) => c.lastBuildStatus === 'running').length,
      stale: list.filter((c) => c.dataStale).length,
      // CSAGE-543: cubes that warrant a human now — a failing build or a
      // stalled feed that is NOT retired on purpose. Internal-admin only.
      attention: (health ?? []).filter(cubeNeedsAttention).length,
    };
  }, [cubes, health]);

  // CSAGE-437: without the acceleration tier there is nothing to build or
  // refresh. Say so rather than offering actions that would fail.
  const tierEnabled = useAccelerationTierEnabled();
  if (tierEnabled === false) {
    return (
      <>
        <header className="page-title">
          <div>
            <h1>Cubes</h1>
          </div>
        </header>
        <div className="empty-state">
          <div className="empty-state-icon">◰</div>
          <h3>Cubes are not available here</h3>
          <p>
            The acceleration tier is not enabled for this deployment, so cubes cannot be built or
            queried. Reports and dashboards built on live datasets are unaffected.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <header className="page-title">
        <div>
          <h1>Cubes</h1>
          <p className="text-dim">
            Sage cube engine — Canopy-owned platform cubes serve every tenant from one physical
            table; customer cubes pull from per-customer warehouses. Both honor the same RLS
            contract.
          </p>
        </div>
      </header>

      {isLoading && <div className="card card-body">Loading…</div>}
      {error && (
        <div className="card">
          <div className="card-body">
            <div className="alert alert--danger">
              Could not load cubes: {(error as Error).message}
            </div>
          </div>
        </div>
      )}

      {cubes && (
        <>
          <div className="row" style={{ gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
            {isInternalAdmin && (
              <Stat
                label="Needs attention"
                value={stats.attention}
                accent={stats.attention > 0 ? 'danger' : undefined}
              />
            )}
            <Stat label="Total" value={stats.total} />
            <Stat label="Platform" value={stats.platform} />
            <Stat label="Customer" value={stats.customer} />
            <Stat
              label="Running"
              value={stats.running}
              accent={stats.running > 0 ? 'info' : undefined}
            />
            <Stat
              label="Failing"
              value={stats.failing}
              accent={stats.failing > 0 ? 'danger' : undefined}
            />
            <Stat
              label="Never built"
              value={stats.neverBuilt}
              accent={stats.neverBuilt > 0 ? 'warning' : undefined}
            />
            <Stat
              label="Stale data"
              value={stats.stale}
              accent={stats.stale > 0 ? 'warning' : undefined}
            />
          </div>

          <div className="row" style={{ gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
            <FilterPill active={tierFilter === 'all'} onClick={() => setTierFilter('all')}>
              All
            </FilterPill>
            <FilterPill
              active={tierFilter === 'platform'}
              onClick={() => setTierFilter('platform')}
            >
              Platform
            </FilterPill>
            <FilterPill
              active={tierFilter === 'customer'}
              onClick={() => setTierFilter('customer')}
            >
              Customer
            </FilterPill>
            {isInternalAdmin && (
              <FilterPill active={attentionOnly} onClick={() => setAttentionOnly((v) => !v)}>
                Needs attention{stats.attention > 0 ? ` (${stats.attention})` : ''}
              </FilterPill>
            )}
          </div>

          {filtered.length === 0 && attentionOnly && (
            <div className="card">
              <div className="card-body">
                <div className="empty-state" style={{ border: 'none', padding: 'var(--space-5)' }}>
                  <div className="empty-state-icon">✓</div>
                  <h3>Nothing needs attention</h3>
                  <p>Every cube is building and its upstream feed is current.</p>
                </div>
              </div>
            </div>
          )}
          {filtered.length === 0 && !attentionOnly && (
            <div className="card">
              <div className="card-body">
                <div className="empty-state" style={{ border: 'none', padding: 'var(--space-5)' }}>
                  <div className="empty-state-icon">◯</div>
                  <h3>No cubes registered</h3>
                  <p>
                    Add a cube definition under
                    <code>apps/api/src/cubes/platform/</code> or
                    <code>apps/api/src/cubes/customers/</code> and redeploy.
                  </p>
                </div>
              </div>
            </div>
          )}

          <BackfillQueuePanel cubes={cubes} />

          {filtered.map((c) => (
            <CubeRow
              key={c.name}
              cube={c}
              health={healthByName.get(c.name)}
              expanded={expanded === c.name}
              onToggle={() => setExpanded((cur) => (cur === c.name ? null : c.name))}
            />
          ))}
        </>
      )}
    </>
  );
}

// ─── Backfill queue (CSAGE-433) ───────────────────────────────────────────
//
// History loads are queued here and drained by the server one idempotent
// window-build chunk per runner tick, only inside the off-peak window.
// Orchestration is entirely server-side — this panel just requests and
// observes it.

function BackfillQueuePanel({ cubes }: { cubes: CubeSummary[] }): JSX.Element {
  const { data, isLoading, error } = useCubeBackfills();
  const create = useCreateCubeBackfill();
  const act = useBackfillAction();
  const [showForm, setShowForm] = useState(false);
  const [cubeName, setCubeName] = useState('');
  const [since, setSince] = useState('2024-10-01');
  const [until, setUntil] = useState(() => new Date().toISOString().slice(0, 10));

  // The server rejects cubes without a watermark column (nothing to
  // window) with a clear error, so the picker lists everything.
  const windowable = cubes;
  const active = (data?.backfills ?? []).filter((b) =>
    ['pending', 'running', 'paused', 'failed'].includes(b.status),
  );
  const recent = (data?.backfills ?? []).filter(
    (b) => !['pending', 'running', 'paused', 'failed'].includes(b.status),
  );

  return (
    <article className="card" style={{ marginBottom: 'var(--space-4)' }}>
      <header className="card-header" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
        <div className="row row--between" style={{ width: '100%' }}>
          <div>
            <h2 style={{ margin: 0 }}>History backfill queue</h2>
            <p className="text-dim text-sm" style={{ margin: '4px 0 0' }}>
              Drained one chunk per minute, only during the off-peak window ({data?.window ?? '…'}
              ).{' '}
              {data?.inWindow ? (
                <span className="badge badge--success">window open</span>
              ) : (
                <span className="badge badge--neutral">window closed</span>
              )}
            </p>
          </div>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => setShowForm((v) => !v)}
          >
            {showForm ? 'Close' : 'Queue backfill'}
          </button>
        </div>

        {showForm && (
          <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap', marginTop: 8 }}>
            <select
              className="input"
              value={cubeName}
              onChange={(e) => setCubeName(e.target.value)}
              aria-label="Cube"
            >
              <option value="">Select cube…</option>
              {windowable.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
            <input
              className="input"
              type="date"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              aria-label="Since"
            />
            <input
              className="input"
              type="date"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              aria-label="Until"
            />
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={!cubeName || create.isPending}
              onClick={() =>
                create.mutate({ cubeName, since, until }, { onSuccess: () => setShowForm(false) })
              }
            >
              {create.isPending ? 'Queueing…' : 'Queue'}
            </button>
            {create.error && (
              <span className="text-sm" style={{ color: 'var(--color-danger)' }}>
                {(create.error as Error).message}
              </span>
            )}
          </div>
        )}

        {isLoading && <div className="text-dim text-sm">Loading queue…</div>}
        {error && <div className="alert alert--danger">{(error as Error).message}</div>}

        {data && active.length === 0 && (
          <div className="text-dim text-sm">
            No active backfills. Last drain: {data.lastDrainOutcome ?? '—'}
          </div>
        )}
        {active.map((b) => (
          <BackfillRow key={b.id} b={b} onAction={(action) => act.mutate({ id: b.id, action })} />
        ))}
        {recent.length > 0 && (
          <details style={{ marginTop: 4 }}>
            <summary className="text-dim text-sm">Finished ({recent.length})</summary>
            {recent.map((b) => (
              <BackfillRow key={b.id} b={b} onAction={() => undefined} readonly />
            ))}
          </details>
        )}
      </header>
    </article>
  );
}

function BackfillRow({
  b,
  onAction,
  readonly,
}: {
  b: CubeBackfill;
  onAction(action: 'pause' | 'resume' | 'cancel'): void;
  readonly?: boolean;
}): JSX.Element {
  const pct = b.chunksTotal > 0 ? Math.round((b.chunksDone / b.chunksTotal) * 100) : 0;
  return (
    <div
      className="row row--between"
      style={{ gap: 'var(--space-3)', padding: '6px 0', alignItems: 'center' }}
    >
      <div style={{ minWidth: 180 }}>
        <code className="text-sm">{b.cubeName}</code>
        <div className="text-dim text-sm">
          {b.since} → {b.until}
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 160 }}>
        <div
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${b.cubeName} backfill progress`}
          style={{
            height: 8,
            borderRadius: 4,
            background: 'var(--color-border-subtle)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: '100%',
              background: b.status === 'failed' ? 'var(--color-danger)' : 'var(--color-accent)',
            }}
          />
        </div>
        <div className="text-dim text-sm" style={{ marginTop: 2 }}>
          {b.chunksDone}/{b.chunksTotal} chunks · cursor {b.cursor}
          {b.lastError ? ` · ${b.lastError.slice(0, 80)}` : ''}
        </div>
      </div>
      <BackfillStatusPill status={b.status} />
      {!readonly && (
        <div className="row" style={{ gap: 'var(--space-1)' }}>
          {(b.status === 'pending' || b.status === 'running') && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => onAction('pause')}
            >
              Pause
            </button>
          )}
          {(b.status === 'paused' || b.status === 'failed') && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => onAction('resume')}
            >
              Resume
            </button>
          )}
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => {
              if (confirm(`Cancel the ${b.cubeName} backfill? Completed chunks stay loaded.`)) {
                onAction('cancel');
              }
            }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

function BackfillStatusPill({ status }: { status: CubeBackfill['status'] }): JSX.Element {
  const cls =
    status === 'running'
      ? 'badge--info'
      : status === 'completed'
        ? 'badge--success'
        : status === 'failed'
          ? 'badge--danger'
          : status === 'paused'
            ? 'badge--warning'
            : 'badge--neutral';
  return <span className={'badge ' + cls}>{status}</span>;
}

// ─── Per-cube row ─────────────────────────────────────────────────────────

function CubeRow({
  cube,
  health,
  expanded,
  onToggle,
}: {
  cube: CubeSummary;
  health?: CubeHealthRow | undefined;
  expanded: boolean;
  onToggle(): void;
}): JSX.Element {
  const trigger = useTriggerCubeBuild(cube.name);

  // CSAGE-543: when we have a diagnosis, a cube that needs a human gets a
  // coloured edge keyed on severity — danger for a failing build, warning for
  // a stalled feed. The per-row diagnosis pill carries the same signal in text.
  const needsAttention = health ? cubeNeedsAttention(health) : false;
  const edgeColor = needsAttention
    ? health?.diagnosis === 'build_failing'
      ? 'var(--color-danger)'
      : 'var(--color-warning)'
    : cube.dataStale
      ? 'var(--color-warning)'
      : undefined;

  return (
    <article
      className="card"
      data-stale={cube.dataStale ? 'true' : undefined}
      data-attention={needsAttention ? 'true' : undefined}
      style={{
        marginBottom: 'var(--space-3)',
        // CSAGE-502/543: a coloured edge marks a cube that wants attention;
        // the status/diagnosis pills carry the same signal in text.
        ...(edgeColor ? { borderLeft: `4px solid ${edgeColor}` } : {}),
      }}
    >
      <header
        className="card-header"
        style={{ flexDirection: 'column', alignItems: 'stretch', gap: 'var(--space-2)' }}
      >
        <div className="row row--between" style={{ width: '100%' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'baseline' }}>
              <h2 style={{ margin: 0 }}>{cube.title}</h2>
              <TierPill tier={cube.tier} />
              <StatusPill status={cube.lastBuildStatus} />
              <DiagnosisPill health={health} />
              <Freshness cube={cube} />
            </div>
            <div className="row" style={{ gap: 'var(--space-2)', marginTop: 4 }}>
              <code className="text-dim text-sm">{cube.name}</code>
              <span className="text-dim text-sm">·</span>
              <span className="text-dim text-sm">{cube.sourceKind}</span>
              <span className="text-dim text-sm">·</span>
              <span className="text-dim text-sm">
                {cube.dimensions.length} dims · {cube.measures.length} measures ·{' '}
                {cube.preAggregations.length} pre-aggs
              </span>
            </div>
            {cube.description && (
              <p className="text-dim text-sm" style={{ marginTop: 6 }}>
                {cube.description}
              </p>
            )}
            {health?.feed && <FeedNote feed={health.feed} />}
          </div>
          <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'flex-start' }}>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => trigger.mutate({ mode: 'incremental' })}
              disabled={trigger.isPending || cube.lastBuildStatus === 'running'}
            >
              {trigger.isPending && trigger.variables?.mode === 'incremental'
                ? 'Refreshing…'
                : 'Refresh now'}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                if (
                  confirm(
                    `Backfill ${cube.name}? This rebuilds the cube from the beginning of time and may take several minutes.`,
                  )
                ) {
                  trigger.mutate({ mode: 'backfill' });
                }
              }}
              disabled={trigger.isPending || cube.lastBuildStatus === 'running'}
            >
              {trigger.isPending && trigger.variables?.mode === 'backfill'
                ? 'Backfilling…'
                : 'Backfill'}
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={onToggle}>
              {expanded ? 'Hide history' : 'History'}
            </button>
          </div>
        </div>

        <div className="row" style={{ gap: 'var(--space-3)' }}>
          <Meta label="Refresh">{cronHumanish(cube.refreshCron)}</Meta>
          <Meta label="Last success">
            {cube.lastSucceededAt ? relativeTime(cube.lastSucceededAt) : '—'}
          </Meta>
          <Meta label="Watermark">{cube.lastSucceededWatermark ?? '—'}</Meta>
          <Meta label="Retention">{cube.retention ?? 'forever'}</Meta>
        </div>

        {trigger.error && (
          <div className="alert alert--danger" style={{ marginTop: 'var(--space-2)' }}>
            <strong>Build trigger failed.</strong>{' '}
            {(trigger.error as Error).message ?? 'Unknown error'}
          </div>
        )}
        {trigger.data && (
          <div className="alert alert--success" style={{ marginTop: 'var(--space-2)' }}>
            <strong>Build complete.</strong> {trigger.data.rowsIngested.toLocaleString()} rows
            ingested in {Math.round(trigger.data.durationMs / 1000)}s ·{' '}
            {trigger.data.preAggregationsRebuilt.length} pre-aggregations rebuilt
          </div>
        )}
      </header>

      {expanded && <BuildHistoryPanel cubeName={cube.name} />}
    </article>
  );
}

// ─── Build history (lazy-loaded) ──────────────────────────────────────────

function BuildHistoryPanel({ cubeName }: { cubeName: string }): JSX.Element {
  const { data, isLoading, error } = useCubeBuilds(cubeName, 25);

  return (
    <div className="card-body" style={{ borderTop: '1px solid var(--color-border-subtle)' }}>
      <h3 style={{ marginTop: 0 }}>Build history</h3>
      {isLoading && <div className="text-dim">Loading…</div>}
      {error && <div className="alert alert--danger">{(error as Error).message}</div>}
      {data && data.length === 0 && <div className="text-dim">No builds yet.</div>}
      {data && data.length > 0 && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Started</th>
              <th>Mode</th>
              <th>Status</th>
              <th>Rows</th>
              <th>Duration</th>
              <th>Watermark range</th>
              <th>Newest data</th>
              <th>By</th>
            </tr>
          </thead>
          <tbody>
            {data.map((b) => (
              <tr key={b.id}>
                <td className="text-sm">{new Date(b.startedAt).toLocaleString()}</td>
                <td>
                  <span className="badge badge--neutral">{b.mode}</span>
                </td>
                <td>
                  <StatusPill status={b.status} />
                </td>
                <td style={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                  {b.rowsIngested.toLocaleString()}
                </td>
                <td style={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                  {b.durationMs !== null
                    ? b.durationMs >= 1000
                      ? `${(b.durationMs / 1000).toFixed(1)}s`
                      : `${b.durationMs}ms`
                    : '—'}
                </td>
                <td className="text-sm text-dim">
                  {b.watermarkFrom ?? '—'} → {b.watermarkTo ?? '—'}
                </td>
                <td className="text-sm">
                  {fmtDataDate(b.newestDataAt)}
                  {b.dataStale ? ' · Stale' : ''}
                </td>
                <td className="text-sm">{b.triggeredBy}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Pills + helpers ──────────────────────────────────────────────────────

function TierPill({ tier }: { tier: 'platform' | 'customer' }): JSX.Element {
  return (
    <span className={'badge ' + (tier === 'platform' ? 'badge--accent' : 'badge--info')}>
      {tier}
    </span>
  );
}

function StatusPill({ status }: { status: CubeBuildStatus | null }): JSX.Element {
  if (status === null) {
    return <span className="badge badge--neutral">never built</span>;
  }
  if (status === 'running') {
    return (
      <span className="badge badge--info">
        <span className="badge-dot" /> running
      </span>
    );
  }
  if (status === 'succeeded') {
    return (
      <span className="badge badge--success">
        <span className="badge-dot" /> succeeded
      </span>
    );
  }
  if (status === 'failed') {
    return <span className="badge badge--danger">failed</span>;
  }
  return <span className="badge badge--warning">{status}</span>;
}

/**
 * CSAGE-543 — the diagnosis that explains WHY, shown only when it adds signal
 * the build-status pill does not. The important case is `source_stalled`: the
 * build succeeds every night but the upstream feed stopped, so the status pill
 * alone reads green. `build_failing` reinforces that the fault is the build,
 * not the feed. The reason string is the hover title.
 */
function DiagnosisPill({ health }: { health?: CubeHealthRow | undefined }): JSX.Element | null {
  if (!health) return null;
  if (health.diagnosis !== 'build_failing' && health.diagnosis !== 'source_stalled') return null;
  const dm = DIAGNOSIS_META[health.diagnosis];
  return (
    <span className={'badge ' + dm.cls} title={health.diagnosisReason}>
      {dm.label}
    </span>
  );
}

/**
 * CSAGE-543 Tier 3 — upstream feed context for the cubes whose source we
 * understand. The lifecycle badge plus the curated note explain a stalled
 * feed that is stale ON PURPOSE (retired under the EJS migration) so an
 * operator does not chase a non-incident.
 */
function FeedNote({ feed }: { feed: NonNullable<CubeHealthRow['feed']> }): JSX.Element {
  const lm = LIFECYCLE_META[feed.lifecycle];
  return (
    <div
      className="row"
      style={{ gap: 'var(--space-2)', alignItems: 'baseline', marginTop: 6, flexWrap: 'wrap' }}
    >
      <span className={'badge ' + lm.cls}>feed {lm.label}</span>
      <span className="text-dim text-sm">{feed.note}</span>
      {feed.ticket && (
        <code className="text-dim text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {feed.ticket}
        </code>
      )}
    </div>
  );
}

/**
 * CSAGE-502 — newest data date, shown next to the build status. A stale
 * cube adds a "Stale" text label that says which limit it crossed. It is
 * not a second badge (standards/status-taxonomy.md rule 2). The text stays
 * in the body color on a warning tint, because warning-colored text fails
 * the 4.5:1 contrast rule (standards/accessibility.md §1).
 */
function Freshness({ cube }: { cube: CubeSummary }): JSX.Element {
  return (
    <span className="text-sm text-dim">
      <span>Newest data {fmtDataDate(cube.newestDataAt)}</span>
      {cube.dataStale && (
        <>
          {' '}
          <strong
            style={{
              background: 'var(--color-warning-soft)',
              padding: '0 var(--space-1)',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            Stale
          </strong>
          <span className="text-dim">
            {' '}
            – older than {cube.staleAfterDays} {cube.staleAfterDays === 1 ? 'day' : 'days'}
          </span>
        </>
      )}
    </span>
  );
}

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick(): void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button type="button" className={'tab' + (active ? ' tab--active' : '')} onClick={onClick}>
      {children}
    </button>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: 'danger' | 'warning' | 'info' | undefined;
}): JSX.Element {
  return (
    <div
      className="card"
      style={{
        padding: 'var(--space-3) var(--space-4)',
        minWidth: 120,
        background:
          accent === 'danger'
            ? 'var(--color-danger-soft)'
            : accent === 'warning'
              ? 'var(--color-warning-soft)'
              : accent === 'info'
                ? 'var(--color-accent-softer)'
                : undefined,
      }}
    >
      <div className="text-dim text-sm">{label}</div>
      <div
        style={{
          fontSize: 'var(--font-size-3xl)',
          fontWeight: 'var(--font-weight-bold)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value.toLocaleString()}
      </div>
    </div>
  );
}

function Meta({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <div
        className="text-dim text-sm"
        style={{
          fontSize: 'var(--font-size-xxs)',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          fontWeight: 'var(--font-weight-bold)',
        }}
      >
        {label}
      </div>
      <div className="text-sm" style={{ fontWeight: 'var(--font-weight-semi)' }}>
        {children}
      </div>
    </div>
  );
}

function cronHumanish(cron: string): string {
  // Hand-rolled. Works for the patterns we use; falls back to the raw cron.
  const parts = cron.split(/\s+/u);
  if (parts.length !== 5) return cron;
  const [m, h, dom, mon, dow] = parts;
  if (dom === '*' && mon === '*' && dow === '*') return `daily at ${pad(h)}:${pad(m)} UTC`;
  if (dom === '*' && mon === '*' && dow !== '*')
    return `weekly on ${dow} at ${pad(h)}:${pad(m)} UTC`;
  return cron;
}

function pad(s: string | undefined): string {
  return (s ?? '').padStart(2, '0');
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.round(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  return `${Math.round(ms / 86_400_000)}d ago`;
}
